import React, { useState, useEffect, useCallback } from "react";
import {
  ArrayInput,
  DateInput,
  email,
  NumberInput,
  required,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
  useCreate,
  useUpdate,
  useNotify,
  usePermissions,
} from "react-admin";
import { useFormContext } from "react-hook-form";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import Map from "../components/Map";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EditIcon from "@mui/icons-material/Edit";
import countryCodes from "../Enterprise/Customer/phone_code.json";
import moment from "moment";
import { checkPhoneExists } from "../components/dataProvider";

export const SelectCustomerInput = (props) => {
  const { watch } = useFormContext();
  const { setValue } = useFormContext();
  const customer = watch("customer");
  const address = watch("address");
  const isInBranch = useFormContext().watch("isInBranch");
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCustomerData, setPendingCustomerData] = useState(null);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [create] = useCreate();

  const postCustomer = async (data) => {
    // Si no hay teléfono o es "0", crear directamente
    if (!data.phone || data.phone === "0") {
      return createCustomerDirect(data);
    }

    setIsCheckingPhone(true);
    try {
      const result = await checkPhoneExists(data.phone);

      if (result.exists) {
        // Mostrar modal de confirmación
        setPendingCustomerData(data);
        setShowConfirmModal(true);
      } else {
        // Crear cliente directamente
        createCustomerDirect(data);
      }
    } catch (error) {
      notify("Error al verificar el teléfono", { type: "error" });
    } finally {
      setIsCheckingPhone(false);
    }
  };

  const createCustomerDirect = async (data) => {
    setLoading(true);
    try {
      await create(
        "enterprise/customers",
        { data },
        {
          onSuccess: (result) => {
            notify("Cliente creado correctamente", { type: "info" });
            setLoading(false);
            handleClose();
            setValue("customer", result);
          },
          onError: (error) => {
            notify("Error al crear cliente", { type: "error" });
            setLoading(false);
          },
        }
      );
    } catch (error) {
      notify(`Error: ${error.message}`, { type: "error" });
      setLoading(false);
    }
  };

  const handleConfirmCreate = () => {
    setShowConfirmModal(false);
    createCustomerDirect(pendingCustomerData);
    setPendingCustomerData(null);
  };

  const handleCancelCreate = () => {
    setShowConfirmModal(false);
    setPendingCustomerData(null);
  };

  return (
    <Box sx={{ width: "50%", ...props.sx }}>
      <CustomCustomerSelector />
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleOpen}
        sx={{
          color: "secondary.menuIcon",
          border: "none",
          backgroundColor: "white",
          "&:hover": {
            border: "none",
          },
          alignSelf: "flex-start",
          marginTop: "1rem",
        }}
      >
        Crear Nuevo Cliente
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ padding: "1rem 1.5rem" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Crear Nuevo Cliente</Typography>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            overflowY: "auto",
            maxHeight: "60vh",
            padding: "1rem 1.5rem",
          }}
        >
          <SimpleForm onSubmit={postCustomer} id="customer-create-form" toolbar={false}>
            <TextInput validate={[required()]} source="fullName" label="Nombre Completo" />
            <SelectInput
              label="Código de País"
              source="countryCode"
              choices={countryCodes.paises}
              optionText={(value) => `${value.name} ( +${value.dial_code} )`}
              optionValue="dial_code"
              validate={[required()]}
              defaultValue={countryCodes.paises.find((e) => e.code === "BO")?.dial_code}
            />
            <NumberInput source="phone" label="Teléfono/Celular" />
            <TextInput validate={[email()]} source="email" label="Email" />
            <DateInput
              label="Fecha de Nacimiento"
              source="birthdate"
              parse={(value) => {
                if (!value) return null;
                return moment(value, "YYYY-MM-DD").toDate(); // devuelve Date
              }}
              format={(value) => {
                if (!value) return null;
                return moment(value).format("YYYY-MM-DD"); // muestra en input
              }}
            />
            <SelectInput
              label="Género"
              source="gender"
              choices={[
                { id: "hombre", name: "Hombre" },
                { id: "mujer", name: "Mujer" },
              ]}
            />
            <ArrayInput source="addresses" label="Direcciones">
              <SimpleFormIterator>
                <Map />
                <TextInput validate={[required()]} source="name" label="Nombre" />
                <TextInput validate={[required()]} source="street" label="Calle" />
                <TextInput source="house" label="Número de casa" />
                <TextInput source="reference" label="Referencia" />
              </SimpleFormIterator>
            </ArrayInput>
          </SimpleForm>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", padding: "1rem" }}>
          <Button onClick={handleClose} color="secondary">
            Cancelar
          </Button>
          <Button
            type="submit"
            form="customer-create-form"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {!isInBranch && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <SelectInput
            source="address"
            label="Dirección"
            choices={customer?.addresses || []}
            disabled={!customer}
            optionText={(value) => value?.name ?? value?.street}
            optionValue="_id"
            validate={[required("La dirección es requerida")]}
          />
          {address && (
            <Map
              source={"address.coordinates"}
              initialLatitude={address?.coordinates?.latitude}
              initialLongitude={address?.coordinates?.longitude}
              disabled
            />
          )}
        </Box>
      )}

      {/* Modal de confirmación para teléfono duplicado */}
      <Dialog open={showConfirmModal} onClose={handleCancelCreate} maxWidth="sm" fullWidth>
        <DialogTitle>Teléfono ya registrado</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Ya existe un cliente registrado con el número de teléfono <strong>{pendingCustomerData?.phone}</strong>.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            ¿Deseas continuar y crear el cliente de todas formas?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCreate} color="secondary" variant="outlined">
            No, cancelar
          </Button>
          <Button onClick={handleConfirmCreate} color="primary" variant="contained" disabled={isCheckingPhone}>
            Sí, continuar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const CustomCustomerSelector = () => {
  const { setValue, watch } = useFormContext();
  const customer = watch("customer");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState(null);
  const [showUpdateConfirmModal, setShowUpdateConfirmModal] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);
  const [isCheckingUpdatePhone, setIsCheckingUpdatePhone] = useState(false);
  const notify = useNotify();
  const [update] = useUpdate();
  const { permissions } = usePermissions();

  const profile = JSON.parse(localStorage.getItem("profile"));
  const providerId = permissions === "admin-sucursal" ? profile.employer._id : null;

  useEffect(() => {
    if (customer) {
      setSelectedCustomer(customer);
      setInputValue(
        `👤 ${customer.fullName}${customer.phone ? `    📞 +${customer.countryCode || "591"} ${customer.phone}` : ""}`
      );
    } else {
      setSelectedCustomer(null);
      setInputValue("");
    }
  }, [customer]);

  const fetchCustomers = useCallback(
    async (searchText = "") => {
      setLoading(true);
      try {
        const filter = {
          $or: [{ fullName: { $regex: searchText, $options: "i" } }, { phone: { $regex: searchText, $options: "i" } }],
        };

        if (providerId) {
          filter.providerId = providerId;
        }

        const url = `${process.env.REACT_APP_API_URL || "/api"}/enterprise/customers?filter=${encodeURIComponent(JSON.stringify(filter))}&page=1&perPage=25&sort=-id`;

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          setCustomers(data.data || []);
        } else {
          notify("Error al cargar clientes", { type: "warning" });
        }
      } catch (error) {
        notify("Error de conexión", { type: "error" });
      } finally {
        setLoading(false);
      }
    },
    [providerId, notify]
  );

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    if (newInputValue.length >= 2 || newInputValue.length === 0) {
      fetchCustomers(newInputValue);
    }
  };

  const handleCustomerChange = (event, newValue) => {
    setSelectedCustomer(newValue);
    setValue("customer", newValue || null);
    if (newValue) {
      setInputValue(
        `👤 ${newValue.fullName}${newValue.phone ? `    📞 +${newValue.countryCode || "591"} ${newValue.phone}` : ""}`
      );
    } else {
      setInputValue("");
    }
  };

  const handleEdit = (customer, event) => {
    event.stopPropagation();
    setCustomerToEdit(customer);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setCustomerToEdit(null);
  };

  const updateCustomer = async (data) => {
    // Si no hay teléfono o es "0", o el teléfono no cambió, actualizar directamente
    if (!data.phone || data.phone === "0" || data.phone === customerToEdit.phone) {
      return updateCustomerDirect(data);
    }

    setIsCheckingUpdatePhone(true);
    try {
      const result = await checkPhoneExists(data.phone);

      if (result.exists) {
        // Mostrar modal de confirmación
        setPendingUpdateData(data);
        setShowUpdateConfirmModal(true);
      } else {
        // Actualizar cliente directamente
        updateCustomerDirect(data);
      }
    } catch (error) {
      notify("Error al verificar el teléfono", { type: "error" });
    } finally {
      setIsCheckingUpdatePhone(false);
    }
  };

  const updateCustomerDirect = async (data) => {
    try {
      await update(
        "enterprise/customers",
        { id: customerToEdit._id, data },
        {
          onSuccess: async (result) => {
            notify("Cliente actualizado correctamente", { type: "info" });
            handleEditClose();

            // traer cliente actualizado desde la API
            const response = await fetch(
              `${process.env.REACT_APP_API_URL || "/api"}/enterprise/customers/${customerToEdit._id}`,
              {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
              }
            );
            const updated = await response.json();

            // actualizar estado local
            setCustomers((prev) => prev.map((c) => (c._id === updated._id ? updated : c)));
            setSelectedCustomer(updated);
            setValue("customer", updated);
            setInputValue(
              `👤 ${updated.fullName}${updated.phone ? `    📞 +${updated.countryCode || "591"} ${updated.phone}` : ""}`
            );
          },
          onFailure: (error) => {
            notify("Error al actualizar cliente", { type: "error" });
          },
        }
      );
    } catch (error) {
      notify(`Error: ${error.message}`, { type: "error" });
    }
  };

  const handleConfirmUpdate = () => {
    setShowUpdateConfirmModal(false);
    updateCustomerDirect(pendingUpdateData);
    setPendingUpdateData(null);
  };

  const handleCancelUpdate = () => {
    setShowUpdateConfirmModal(false);
    setPendingUpdateData(null);
  };

  return (
    <>
      <Autocomplete
        options={customers}
        loading={loading}
        value={selectedCustomer}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleCustomerChange}
        getOptionLabel={(option) =>
          `👤 ${option.fullName}${option.phone ? `    📞 +${option.countryCode || "591"} ${option.phone}` : ""}`
        }
        isOptionEqualToValue={(option, value) => {
          if (!option || !value) return false;
          return option._id === value._id;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Cliente *"
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                fontSize: "1rem",
                fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                padding: "0 !important",
              },
              "& .MuiInputLabel-root": {
                fontSize: "1rem",
                fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
              },
              "& .MuiInputBase-input": {
                fontSize: "1rem",
                fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                padding: "15px 14px !important",
              },
              "& .MuiAutocomplete-input": {
                padding: "15px 14px !important",
              },
              "& .MuiAutocomplete-inputRoot": {
                padding: "0 !important",
              },
              "& .MuiInputBase-inputAdornedEnd": {
                padding: "15px 14px !important",
                paddingRight: "32px !important",
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 12px",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <PersonIcon sx={{ color: "#666", marginRight: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {option.fullName}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {option.phone && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <PhoneIcon sx={{ color: "#666", fontSize: "16px", marginRight: 0.5 }} />
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    +{option.countryCode || "591"} {option.phone}
                  </Typography>
                </Box>
              )}

              <IconButton
                size="small"
                onClick={(event) => handleEdit(option, event)}
                sx={{
                  padding: "4px",
                  color: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#e3f2fd",
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}
        noOptionsText="No se encontraron clientes"
        clearOnBlur={false}
        disableClearable={false}
        freeSolo={false}
      />

      {/* Dialog para editar cliente */}
      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ padding: "1rem 1.5rem" }}>
          <Typography variant="h6">Editar Cliente</Typography>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            overflowY: "auto",
            maxHeight: "60vh",
            padding: "1rem 1.5rem",
          }}
        >
          {customerToEdit && (
            <SimpleForm
              onSubmit={updateCustomer}
              id="customer-edit-form"
              toolbar={false}
              defaultValues={customerToEdit}
            >
              <TextInput validate={[required()]} source="fullName" label="Nombre Completo" />
              <SelectInput
                label="Código de País"
                source="countryCode"
                choices={countryCodes.paises}
                optionText={(value) => `${value.name} ( +${value.dial_code} )`}
                optionValue="dial_code"
                validate={[required()]}
              />
              <NumberInput source="phone" label="Teléfono/Celular" />
              <TextInput validate={[email()]} source="email" label="Email" />
              <DateInput
                label="Fecha de Nacimiento"
                source="birthdate"
                parse={(value) => {
                  if (!value) return null;
                  return moment(value, "YYYY-MM-DD").toDate();
                }}
                format={(value) => {
                  if (!value) return null;
                  return moment(value).format("YYYY-MM-DD");
                }}
              />
              <SelectInput
                label="Género"
                source="gender"
                choices={[
                  { id: "hombre", name: "Hombre" },
                  { id: "mujer", name: "Mujer" },
                ]}
              />
              <ArrayInput source="addresses" label="Direcciones">
                <SimpleFormIterator>
                  <Map />
                  <TextInput validate={[required()]} source="name" label="Nombre" />
                  <TextInput validate={[required()]} source="street" label="Calle" />
                  <TextInput source="house" label="Número de casa" />
                  <TextInput source="reference" label="Referencia" />
                </SimpleFormIterator>
              </ArrayInput>
            </SimpleForm>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", padding: "1rem" }}>
          <Button onClick={handleEditClose} color="secondary">
            Cancelar
          </Button>
          <Button type="submit" form="customer-edit-form" variant="contained" color="primary" startIcon={<SaveIcon />}>
            Actualizar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación para teléfono duplicado en update */}
      <Dialog open={showUpdateConfirmModal} onClose={handleCancelUpdate} maxWidth="sm" fullWidth>
        <DialogTitle>Teléfono ya registrado</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Ya existe un cliente registrado con el número de teléfono <strong>{pendingUpdateData?.phone}</strong>.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            ¿Deseas continuar y actualizar el cliente de todas formas?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUpdate} color="secondary" variant="outlined">
            No, cancelar
          </Button>
          <Button onClick={handleConfirmUpdate} color="primary" variant="contained" disabled={isCheckingUpdatePhone}>
            Sí, continuar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
